import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { ApiError, apiVoid } from '@/lib/apiClient';

/**
 * SLICE 14c — CLEAR a tenant user's command PIN.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 RESET, NEVER SET, NEVER READ — AND THE MISSING VERBS ARE THE SECURITY.
 * ─────────────────────────────────────────────────────────────────────────────
 * The wire has exactly one route here: `POST /api/admin/users/:userId/command-pin/
 * reset`, a 204 that CLEARS the PIN. There is deliberately no route that sets a
 * PIN and none that reads one, because either would mean Raad holds the secret
 * that authorises blocking a customer's vehicle — and a second factor the vendor
 * also holds is not a second factor. So this hook exists only in the clearing
 * direction, and the dialog it serves must not imply otherwise.
 *
 * A 204 with an empty body, so `apiVoid` — `apiJson` would call `res.json()` on
 * the empty body and turn a successful clear into a parse error (the 17x defect).
 */
export type CommandPinResetFailure = 'not_found' | 'unknown';

export function classifyPinResetError(error: unknown): CommandPinResetFailure {
  // 404 — no user with that id (a staff id lands here too: staff have no command
  // PIN). Never 403 for a missing user, so a 404 is unambiguously "no such user".
  if (error instanceof ApiError && error.status === 404) return 'not_found';
  return 'unknown';
}

export function useCommandPinReset(): UseMutationResult<void, Error, string> {
  return useMutation({
    mutationFn: (userId: string) =>
      apiVoid(`/api/admin/users/${encodeURIComponent(userId)}/command-pin/reset`, {
        method: 'POST',
      }),
  });
}
